import React, { useEffect, useRef, useState } from 'react';
import useStore from '@/store';
import { logger } from '@/utils/logger';

/**
 * Handles the return from Stripe Checkout.
 *
 * Lifecycle:
 *   1. Mount on app bootstrap.
 *   2. Read `?checkout=...` from window.location.search.
 *      - `?checkout=success`: user just completed Checkout. Poll
 *        /api/billing/me every 2s for up to 30s waiting for the
 *        webhook-driven transition from 'trialing' to 'active'.
 *      - `?checkout=cancel`: user bailed on Stripe. Do nothing special,
 *        we'll stay on whatever screen the router decided (likely the
 *        paywall they came from).
 *   3. Clean the query string with history.replaceState so a refresh
 *      doesn't re-trigger the flow.
 *
 * Critical: this component NEVER writes status='active' locally.
 * Activation is exclusively webhook-driven server-side. The frontend
 * only *observes* the state change via /me polling. If the webhook
 * never arrives (30s timeout), we show a "payment received, activation
 * in progress" message — never a "successful" claim without the webhook.
 */
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

type LocalStatus = 'idle' | 'polling' | 'timeout';

const CheckoutReturnHandler: React.FC = () => {
  const fetchSubscription = useStore((s) => s.fetchSubscription);
  const beginCheckoutPolling = useStore((s) => s.beginCheckoutPolling);
  const endCheckoutPolling = useStore((s) => s.endCheckoutPolling);
  const setCurrentScreen = useStore((s) => s.setCurrentScreen);
  const checkoutPolling = useStore((s) => s.checkoutPolling);
  const checkoutPollTimedOut = useStore((s) => s.checkoutPollTimedOut);

  const startedRef = useRef<boolean>(false);
  const [status, setStatus] = useState<LocalStatus>('idle');

  useEffect(() => {
    if (startedRef.current) return;
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const checkoutParam = params.get('checkout');
    if (!checkoutParam) return;

    startedRef.current = true;

    // Scrub the query string so a refresh or link share doesn't re-trigger
    // the handler with stale state.
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, cleanUrl);

    if (checkoutParam !== 'success') {
      // cancel / unknown — nothing to poll. The paywall screen (if the
      // user was on one) is still there.
      logger.info('Checkout return: not success, no polling', { checkoutParam });
      return;
    }

    logger.info('Checkout return: success — starting /me poll');
    beginCheckoutPolling();
    setStatus('polling');

    const startedAt = Date.now();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const poll = async (): Promise<void> => {
      if (cancelled) return;
      const payload = await fetchSubscription();
      if (cancelled) return;

      if (payload && payload.status === 'active') {
        logger.info('Checkout return: subscription active, routing to camera');
        endCheckoutPolling(false);
        setStatus('idle');
        setCurrentScreen('camera');
        return;
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        logger.warn('Checkout return: poll timed out (webhook likely delayed)');
        endCheckoutPolling(true);
        setStatus('timeout');
        return;
      }

      timer = setTimeout(() => {
        void poll();
      }, POLL_INTERVAL_MS);
    };

    void poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // No query param + not polling + not timed-out → render nothing.
  if (status === 'idle' && !checkoutPolling && !checkoutPollTimedOut) {
    return null;
  }

  // Full-viewport interstitial while polling or after timeout.
  return (
    <div
      data-testid="checkout-return-handler"
      role="status"
      aria-live="polite"
      className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center text-yellow-400 p-4"
    >
      {status === 'polling' || checkoutPolling ? (
        <>
          <div className="w-12 h-12 border-4 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin mb-4" />
          <h2 className="text-xl font-bold font-mono mb-2">Activating your subscription…</h2>
          <p className="text-sm text-yellow-400/80 font-mono text-center max-w-md">
            Confirming your payment with Stripe. This should only take a few seconds.
          </p>
        </>
      ) : (
        <>
          <h2 className="text-xl font-bold font-mono mb-2">Payment received</h2>
          <p className="text-sm text-yellow-400/80 font-mono text-center max-w-md mb-4">
            Activation is taking longer than usual. Try again in a minute — if the
            issue persists, your payment was still recorded by Stripe and will go
            through.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-5 py-2 bg-yellow-400 text-black font-mono font-bold rounded-lg hover:bg-yellow-300"
          >
            Refresh
          </button>
        </>
      )}
    </div>
  );
};

export default CheckoutReturnHandler;
